import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

const clean = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const DEFAULT_TABS = [
  {
    id: "room_board",
    label: "Room Board",
    icon: "🏨",
  },
  {
    id: "guest_crm",
    label: "Guest CRM",
    icon: "👤",
  },
  {
    id: "pending_payments",
    label: "Pending Payments",
    icon: "💰",
  },
  {
    id: "bookings",
    label: "Bookings",
    icon: "📋",
  },
  {
    id: "booking_diary",
    label: "Booking Diary",
    icon: "📔",
  },
  {
    id: "day_book",
    label: "Day Book",
    icon: "📒",
  },
  {
    id: "expenses",
    label: "Expenses",
    icon: "💸",
  },
  {
    id: "reports",
    label: "Reports",
    icon: "📊",
  },
  {
    id: "audit_logs",
    label: "Audit Logs",
    icon: "🛡",
  },
];

const getStoredTab = (
  storageKey,
  fallbackTab
) => {
  try {
    const storedTab =
      localStorage.getItem(storageKey);

    return clean(storedTab) || fallbackTab;
  } catch (error) {
    return fallbackTab;
  }
};

/* -------------------------------------------------------------------------- */
/*                         Manager Dashboard Navigation                       */
/* -------------------------------------------------------------------------- */

function ManagerDashboardNav({
  hotelId,

  activeTab,
  defaultTab = "room_board",

  tabs = DEFAULT_TABS,

  counts = {},

  disabledTabs = [],

  onChange,
}) {
  const storageKey = useMemo(
    () =>
      `manager-dashboard-active-tab-${
        clean(hotelId) || "default"
      }`,
    [hotelId]
  );

  const availableTabs = useMemo(
    () =>
      tabs.filter(
        (tab) =>
          tab &&
          clean(tab.id) &&
          clean(tab.label)
      ),
    [tabs]
  );

  const firstAvailableTab =
    availableTabs.find(
      (tab) =>
        !disabledTabs.includes(tab.id)
    )?.id || defaultTab;

  const [
    internalActiveTab,
    setInternalActiveTab,
  ] = useState(() => {
    const storedTab = getStoredTab(
      storageKey,
      defaultTab
    );

    const storedTabExists =
      availableTabs.some(
        (tab) =>
          tab.id === storedTab &&
          !disabledTabs.includes(tab.id)
      );

    return storedTabExists
      ? storedTab
      : firstAvailableTab;
  });

  const selectedTab =
    clean(activeTab) ||
    internalActiveTab;

  /* ------------------------------------------------------------------------ */
  /*                         Keep Valid Active Tab                             */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    const selectedTabExists =
      availableTabs.some(
        (tab) =>
          tab.id === selectedTab &&
          !disabledTabs.includes(tab.id)
      );

    if (!selectedTabExists) {
      setInternalActiveTab(
        firstAvailableTab
      );

      onChange?.(
        firstAvailableTab
      );
    }
  }, [
    selectedTab,
    availableTabs,
    disabledTabs,
    firstAvailableTab,
    onChange,
  ]);

  /* ------------------------------------------------------------------------ */
  /*                              Persist Tab                                  */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    try {
      localStorage.setItem(
        storageKey,
        selectedTab
      );
    } catch (error) {
      console.warn(
        "Dashboard active tab save nahi hua:",
        error
      );
    }
  }, [storageKey, selectedTab]);

  /* ------------------------------------------------------------------------ */
  /*                             Change Tab                                    */
  /* ------------------------------------------------------------------------ */

  const changeTab = (tabId) => {
    if (
      disabledTabs.includes(tabId)
    ) {
      return;
    }

    setInternalActiveTab(tabId);

    onChange?.(tabId);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* ------------------------------------------------------------------------ */
  /*                           Keyboard Navigation                             */
  /* ------------------------------------------------------------------------ */

  const handleKeyDown = (
    event,
    currentIndex
  ) => {
    const enabledTabs =
      availableTabs.filter(
        (tab) =>
          !disabledTabs.includes(tab.id)
      );

    const currentEnabledIndex =
      enabledTabs.findIndex(
        (tab) =>
          tab.id ===
          availableTabs[currentIndex]?.id
      );

    if (
      currentEnabledIndex < 0
    ) {
      return;
    }

    let nextIndex =
      currentEnabledIndex;

    if (
      event.key === "ArrowRight"
    ) {
      nextIndex =
        (currentEnabledIndex + 1) %
        enabledTabs.length;
    } else if (
      event.key === "ArrowLeft"
    ) {
      nextIndex =
        (currentEnabledIndex -
          1 +
          enabledTabs.length) %
        enabledTabs.length;
    } else if (
      event.key === "Home"
    ) {
      nextIndex = 0;
    } else if (
      event.key === "End"
    ) {
      nextIndex =
        enabledTabs.length - 1;
    } else {
      return;
    }

    event.preventDefault();

    const nextTab =
      enabledTabs[nextIndex];

    changeTab(nextTab.id);

    document
      .getElementById(
        `manager-tab-${nextTab.id}`
      )
      ?.focus();
  };

  /* ------------------------------------------------------------------------ */
  /*                                  Render                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <nav
      style={navWrapperStyle}
      aria-label="Manager Dashboard Sections"
    >
      <div style={mobileTitleStyle}>
        Dashboard Sections
      </div>

      <div
        role="tablist"
        aria-orientation="horizontal"
        style={tabsContainerStyle}
      >
        {availableTabs.map(
          (tab, index) => {
            const isActive =
              selectedTab === tab.id;

            const isDisabled =
              disabledTabs.includes(
                tab.id
              );

            const count =
              Number(counts?.[tab.id]);

            const hasCount =
              Number.isFinite(count) &&
              count > 0;

            return (
              <button
                key={tab.id}
                id={`manager-tab-${tab.id}`}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls={`manager-panel-${tab.id}`}
                tabIndex={
                  isActive ? 0 : -1
                }
                disabled={isDisabled}
                onClick={() =>
                  changeTab(tab.id)
                }
                onKeyDown={(event) =>
                  handleKeyDown(
                    event,
                    index
                  )
                }
                style={{
                  ...tabButtonStyle,

                  background: isActive
                    ? "#1d4ed8"
                    : "#ffffff",

                  color: isActive
                    ? "#ffffff"
                    : "#334155",

                  borderColor: isActive
                    ? "#1d4ed8"
                    : "#cbd5e1",

                  opacity: isDisabled
                    ? 0.45
                    : 1,

                  cursor: isDisabled
                    ? "not-allowed"
                    : "pointer",

                  boxShadow: isActive
                    ? "0 7px 18px rgba(29,78,216,.22)"
                    : "none",
                }}
              >
                <span
                  style={tabIconStyle}
                  aria-hidden="true"
                >
                  {tab.icon || "•"}
                </span>

                <span style={tabLabelStyle}>
                  {tab.label}
                </span>

                {hasCount && (
                  <span
                    style={{
                      ...countBadgeStyle,

                      background: isActive
                        ? "rgba(255,255,255,.22)"
                        : getCountBackground(
                            tab.id
                          ),

                      color: isActive
                        ? "#ffffff"
                        : getCountColor(
                            tab.id
                          ),
                    }}
                  >
                    {count > 99
                      ? "99+"
                      : count}
                  </span>
                )}
              </button>
            );
          }
        )}
      </div>
    </nav>
  );
}

/* -------------------------------------------------------------------------- */
/*                              Tab Panel Wrapper                             */
/* -------------------------------------------------------------------------- */

export function ManagerDashboardTabPanel({
  id,
  activeTab,
  children,
  keepMounted = false,
}) {
  const isActive =
    id === activeTab;

  if (
    !isActive &&
    !keepMounted
  ) {
    return null;
  }

  return (
    <section
      id={`manager-panel-${id}`}
      role="tabpanel"
      aria-labelledby={`manager-tab-${id}`}
      hidden={!isActive}
      style={{
        ...panelWrapperStyle,

        display: isActive
          ? "block"
          : "none",
      }}
    >
      {children}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*                             Count Badge Theme                              */
/* -------------------------------------------------------------------------- */

function getCountBackground(tabId) {
  if (
    tabId === "pending_payments"
  ) {
    return "#fee2e2";
  }

  if (
    tabId === "day_book"
  ) {
    return "#fef3c7";
  }

  return "#dbeafe";
}

function getCountColor(tabId) {
  if (
    tabId === "pending_payments"
  ) {
    return "#b91c1c";
  }

  if (
    tabId === "day_book"
  ) {
    return "#92400e";
  }

  return "#1d4ed8";
}

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

const navWrapperStyle = {
  position: "sticky",
  top: 0,
  zIndex: 900,

  marginBottom: 18,
  padding: "10px 0",

  background:
    "rgba(248,250,252,.96)",

  borderBottom:
    "1px solid #e2e8f0",

  backdropFilter: "blur(14px)",
};

const mobileTitleStyle = {
  marginBottom: 8,

  color: "#64748b",

  fontSize: 10,
  fontWeight: 900,

  textTransform: "uppercase",
  letterSpacing: ".08em",
};

const tabsContainerStyle = {
  display: "flex",

  gap: 8,

  paddingBottom: 4,

  overflowX: "auto",

  scrollbarWidth: "thin",
};

const tabButtonStyle = {
  position: "relative",

  flex: "0 0 auto",

  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",

  gap: 7,

  minHeight: 42,

  padding: "9px 12px",

  border:
    "1px solid #cbd5e1",

  borderRadius: 11,

  fontWeight: 900,
  fontSize: 11,

  whiteSpace: "nowrap",

  transition:
    "background .15s ease, color .15s ease, border-color .15s ease, box-shadow .15s ease",
};

const tabIconStyle = {
  fontSize: 15,
  lineHeight: 1,
};

const tabLabelStyle = {
  lineHeight: 1.2,
};

const countBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",

  minWidth: 21,
  height: 21,

  padding: "0 6px",

  borderRadius: 999,

  fontSize: 9,
  fontWeight: 900,
};

const panelWrapperStyle = {
  animation:
    "managerPanelFade .18s ease",
};

export default ManagerDashboardNav;